/*
* Copyright (C) Regione Lombardia
*/
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { doDownload, REGEX_DIGIT } from '@core/utilities';
import { environment } from '@env/environment';
import {
    AnagraficaComuni,
    DocumentoOpera, Manutenzione,
    Monitoraggio,
    Opera,
    OperaSecondaria,
    PianoInterventi,
    RequestOperaSearch,
    ResponseOpera,
    Rilevamento
} from '@models';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { UtilsService } from './utils.service';

@Injectable({
    providedIn: 'root'
})
export class OperaService {

    private _pageType: string;
    get pageTypeSession() { return this._pageType; }
    set pageTypeSession(pageTypeSession: string) { this._pageType = pageTypeSession; }

    private _opera: Opera;
    get operaSession() { return this._opera; }
    set operaSession(opera: Opera) { this._opera = opera; }

    private eventSource: EventSource;

    private _dataOperaChanged: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    get dataOperaChanged() { return this._dataOperaChanged.getValue(); }
    set dataOperaChanged(dataChanged: boolean) { this._dataOperaChanged.next(dataChanged); }

    constructor(
        private httpClient: HttpClient,
        private fb: FormBuilder,
        private utils: UtilsService
    ) { }

    /* Form: group */
    public generateFormRicercaOpera(values?: RequestOperaSearch): FormGroup {
        const criteriaForm = this.fb.group({
            codProv: this.fb.control(null),
            comune: this.fb.control(null),
            codIstat: this.fb.control(null),
            idTipologiaOpera: this.fb.control(null),
            idTipoIntervento: this.fb.control(null),
            provenienza: this.fb.control(null),
            idOpera: this.fb.control(null),
            idMaterialeCostruzione: this.fb.control(null),
            idStato: this.fb.control(null),
            trattoIdrico: this.fb.control(null),
            /*progetto */
            cup: this.fb.control(null)
        });
        if (values) {
            criteriaForm.patchValue(values);
        }
        return criteriaForm;
    }
    public generateFormOpera(values?: Opera): FormGroup {
        const criteriaForm = this.fb.group({
            /* Sistema */
            idOpera: this.fb.control(null),
            idOperaOld: this.fb.control({value: null, disabled: true}),
            tipoInserimento: this.fb.control(null),
            idStato: this.fb.control(null),
            idRilievo: this.fb.control(null),
            provenienza: this.fb.control({value: null, disabled: true}),
            /* Anagrafica dell'opera */
            codProv: this.fb.control(null, Validators.required),
            codIstat: this.fb.control({value: null, disabled: true}, Validators.required),
            comune: this.fb.control({value: null, disabled: true}, Validators.required), /* Campo di supporto FE */
            localita: this.fb.control(null),
            bacinoIdrografico: this.fb.control({value: null, disabled: true}),
            trattoIdrico: this.fb.control({value: null, disabled: true}),
            // idSponda: this.fb.control({value: null, disabled: true}),
            tipoShape: this.fb.control({value: null, disabled: true}),
            sottoBacino: this.fb.control({value: null, disabled: true}),
            macroTipologiaOpera: this.fb.control({value: null, disabled: true}), /* Campo di supporto FE */
            classificazioneOpera: this.fb.control(null, Validators.required),
            idTipologiaOpera: this.fb.control(null, Validators.required),
            idImportanza: this.fb.control(null),
            note: this.fb.control(null),
            interventoEmergenza: this.fb.control(null),
            idFunzionePrevalente: this.fb.control({value: null, disabled: true}),
            /* Dati costruttivi dell'opera */
            enteProprietarioGestore: this.fb.control(null),
            enteManutentore: this.fb.control(null),
            annoFineEsistenza: this.fb.control(null,[Validators.pattern('^(19|20)[0-9]{2}$')]),
            idMaterialeCostruzione: this.fb.control(null),
            idMaterialeRivestimento: this.fb.control(null),
            quotaMaxMetri: this.fb.control(null, [Validators.pattern(REGEX_DIGIT)]),
            quotaMedMetri: this.fb.control(null, [Validators.pattern(REGEX_DIGIT)]),
            quotaMinMetri: this.fb.control(null, [Validators.pattern(REGEX_DIGIT)]),
            altezzaMetri: this.fb.control(null),
            lunghezzaMetri: this.fb.control(null),
            larghezzaMetri: this.fb.control(null),
            volumeMC: this.fb.control(null),
            areaMQ: this.fb.control(null),
            idOperaOrig: this.fb.control(null)
        });
        if (values) {
            criteriaForm.patchValue(values);
            if(criteriaForm.get('codIstat')?.value) {
                criteriaForm.get('comune')?.setValue(criteriaForm.get('codIstat')?.value);
            }
        }
        return criteriaForm;
    }

    setRequiredValidators(form: FormGroup) {
        if(form.get('tipoInserimento')?.value !== null && form.get('tipoInserimento')?.value !== undefined && form.get('tipoInserimento')?.value !== 2) {
            form.get('enteProprietarioGestore')?.addValidators(Validators.required);
            form.get('enteProprietarioGestore')?.updateValueAndValidity();
        }
    }

    /* Form utilities */
    public comuneFormatter = (result: AnagraficaComuni) => result.descrizione;

    /* Ricerca */
    public search(values: RequestOperaSearch, paginatorQueryParams: string): Observable<ResponseOpera> {
        delete values.comune; /* Campo utilizzato solo da Angular */
        return this.httpClient.post<ResponseOpera>(`${environment.urlApi}/private/opera/ricerca/amministratore?${paginatorQueryParams}`, values);
    }
    public searchApprovaOpera(values: RequestOperaSearch, paginatorQueryParams: string): Observable<ResponseOpera> {
        delete values.comune; /* Campo utilizzato solo da Angular */
        return this.httpClient.post<ResponseOpera>(`${environment.urlApi}/private/opera/ricerca/da-approvare?${paginatorQueryParams}`, values);
    }
    public searchExport(values: RequestOperaSearch, paginatorQueryParams: string) {
        delete values.comune; /* Campo utilizzato solo da Angular */
        const today = new Date().toLocaleDateString('it-IT');
        return this.httpClient.post(`${environment.urlApi}/private/opera/ricerca/export?${paginatorQueryParams}`, values, {
            responseType: 'blob'
        }).pipe(
            tap((response) => doDownload(response, `ODS-export_ricerca_opere-${today}`, 'xlsx'))
        );
    }

    /* Opera: CRUD */
    public create(params: Opera): Observable<Opera> {
        return this.httpClient.post<Opera>(`${environment.urlApi}/private/opera`, params);
    }
    public read(idOpera: number): Observable<Opera> {
        return this.httpClient.get<Opera>(`${environment.urlApi}/private/opera/${idOpera}`);
    }
    public update(params: Opera): Observable<Opera> {
        delete params.comune;
        return this.httpClient.put<Opera>(`${environment.urlApi}/private/opera`, params);
    }
    public delete(idOpera: number) {
        return this.httpClient.delete(`${environment.urlApi}/private/opera/${idOpera}`, { observe: 'response' });
    }

    /* Opera: Workflow */
    public workflowNext(idOpera: number, idStatoToBe: number) {
        return this.httpClient.get(`${environment.urlApi}/private/opera/${idOpera}/workflow/next/stato/${idStatoToBe}`, { observe: 'response' });
    }

    /* Documento opera: CRUD */
    public documentoOperaByIdOpera(idOpera: number): Observable<DocumentoOpera[]> {
        return this.httpClient.get<DocumentoOpera[]>(`${environment.urlApi}/private/opera/${idOpera}/documento-opera`);
    }
    public insertDocumento(file: any,idOpera: number, documento: DocumentoOpera): Observable<any> {
        const fileData = new FormData();
        fileData.append('file', file);
        fileData.append('didascalia', documento.didascalia);
        fileData.append('idClasseDocumento', documento.idClasseDocumento.toString());
        fileData.append('docPublic', documento.docPublic.toString());
        return this.httpClient.post<DocumentoOpera>(`${environment.urlApi}/private/documento-opera/upload/${idOpera}`, fileData);
    }
    public deleteDocumento(idDocumentoProgetto: number): Observable<any> {
        return this.httpClient.delete<DocumentoOpera>(`${environment.urlApi}/private/documento-opera/${idDocumentoProgetto}`);
    }
    public updateDocumentoOpera(documento: DocumentoOpera): Observable<any> {
        return this.httpClient.put<DocumentoOpera>(`${environment.urlApi}/private/documento-opera`, {
            idDocumento: documento.idDocumento,
            idClasseDocumento: documento.idClasseDocumento,
            didascalia: documento.didascalia,
            docPublic: documento.docPublic
        });
    }
    public downloadDocumento(item: DocumentoOpera) {
        return this.httpClient.get(`${environment.urlApi}/private/documento-opera/download/${item.idDocumento}`, {
            responseType: 'blob'
        }).pipe(
            tap((response) =>
                doDownload(
                    response,
                    item.nomeDocumento.substring(0, item.nomeDocumento.indexOf('.')),
                    item.nomeDocumento.substring(item.nomeDocumento.indexOf('.') + 1)
                )
            )
        );
    }

    public downloadOpera(idOpera: number) {
        return this.httpClient.get(`${environment.urlApi}/private/opera/${idOpera}/export`, {
            responseType: 'blob'
        }).pipe(
            tap((response) =>
                doDownload(
                    response,
                    'opera-' + idOpera,
                    'pdf'
                )
            )
        );
    }

    /* Opera secondaria: CRUD */
    public operaSecondariaByIdOpera(idOpera: number): Observable<OperaSecondaria[]>{
        return this.httpClient.get<OperaSecondaria[]>(`${environment.urlApi}/private/opera/${idOpera}/opera-secondaria`);
    }
    public insertOperaSecondaria(params: OperaSecondaria): Observable<OperaSecondaria> {
        return this.httpClient.post<OperaSecondaria>(`${environment.urlApi}/private/opera-secondaria`, params);
    }
    public updateOperaSecondaria(params: OperaSecondaria): Observable<OperaSecondaria> {
        return this.httpClient.put<OperaSecondaria>(`${environment.urlApi}/private/opera-secondaria`, params);
    }
    public deleteOperaSecondaria(idOperaSecondaria: number) {
        return this.httpClient.delete(`${environment.urlApi}/private/opera-secondaria/${idOperaSecondaria}`, { observe: 'response' });
    }

    /* Sopralluogo/Rilevamento: CRUD */
    public rilevamentoByIdOpera(idOpera: number): Observable<Rilevamento[]> {
        return this.httpClient.get<Rilevamento[]>(`${environment.urlApi}/private/opera/${idOpera}/rilevamento`);
    }
    public insertRilevamento(params: Rilevamento): Observable<Rilevamento> {
        return this.httpClient.post<Rilevamento>(`${environment.urlApi}/private/rilevamento`, params);
    }
    public updateRilevamento(params: Rilevamento): Observable<Rilevamento> {
        return this.httpClient.put<Rilevamento>(`${environment.urlApi}/private/rilevamento`, params);
    }
    public deleteRilevamento(idRilevamento: number) {
        return this.httpClient.delete(`${environment.urlApi}/private/rilevamento/${idRilevamento}`, { observe: 'response' });
    }

    /* Monitoraggio: CRUD */
    public monitoraggioByIdOpera(idOpera: number): Observable<Monitoraggio[]> {
        return this.httpClient.get<Monitoraggio[]>(`${environment.urlApi}/private/opera/${idOpera}/monitoraggio`);
    }
    public insertMonitoraggio(params: Monitoraggio): Observable<Monitoraggio> {
        return this.httpClient.post<Monitoraggio>(`${environment.urlApi}/private/monitoraggio`, params);
    }
    public updateMonitoraggio(params: Monitoraggio): Observable<Monitoraggio> {
        return this.httpClient.put<Monitoraggio>(`${environment.urlApi}/private/monitoraggio`, params);
    }
    public deleteMonitoraggio(idMonitoraggio: number) {
        return this.httpClient.delete(`${environment.urlApi}/private/monitoraggio/${idMonitoraggio}`, { observe: 'response' });
    }

    /* Manutenzione: CRUD */
    public manutenzioneByIdOpera(idOpera: number): Observable<Manutenzione[]> {
        return this.httpClient.get<Manutenzione[]>(`${environment.urlApi}/private/opera/${idOpera}/manutenzione`);
    }
    public insertManutenzione(params: Manutenzione): Observable<Manutenzione> {
        return this.httpClient.post<Manutenzione>(`${environment.urlApi}/private/manutenzione`, params);
    }
    public updateManutenzione(params: Manutenzione): Observable<Manutenzione> {
        return this.httpClient.put<Manutenzione>(`${environment.urlApi}/private/manutenzione`, params);
    }
    public deleteManutenzione(idManutenzione: number) {
        return this.httpClient.delete(`${environment.urlApi}/private/manutenzione/${idManutenzione}`, { observe: 'response' });
    }

    /* Piano Interventi: CRUD */
    public pianoInterventiByIdOpera(idOpera: number): Observable<PianoInterventi[]>{
        return this.httpClient.get<PianoInterventi[]>(`${environment.urlApi}/private/opera/${idOpera}/piano-interventi`);
    }
    public insertPianoInterventi(params: PianoInterventi): Observable<PianoInterventi> {
        return this.httpClient.post<PianoInterventi>(`${environment.urlApi}/private/piano-intervento`, params);
    }
    public updatePianoInterventi(params: PianoInterventi): Observable<PianoInterventi> {
        return this.httpClient.put<PianoInterventi>(`${environment.urlApi}/private/piano-intervento`, params);
    }
    public deletePianoInterventi(idPianoIntervento: number) {
        return this.httpClient.delete(`${environment.urlApi}/private/piano-intervento/${idPianoIntervento}`, { observe: 'response' });
    }

    public ripristinaInBozza(idOpera: number, idUtente: number): Observable<boolean> {
        return this.httpClient.put<boolean>(`${environment.urlApi}/private/opera/${idOpera}/${idUtente}//ripristinaInBozza`, null );
    }

    public ricercaOperaOperatore(values: RequestOperaSearch, idUtente: number, paginatorQueryParams: string): Observable<ResponseOpera> {
        delete values.comune; // Campo utilizzato solo da Angular
        return this.httpClient.post<ResponseOpera>(
            `${environment.urlApi}/private/opera/ricerca/operatore?idUtente=${idUtente}&${paginatorQueryParams}`,
            values
        );
    }

}
